import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/defer';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/finally';
import { environment } from '../../../environments/environment';
import { establecerVersionSentry } from './sentry';

/**
 * TR-22 (B2): métricas de uso y tiempos medidos en el navegador.
 *
 * Acumula mediciones en memoria y las manda por lote (cada ~30 s y al cerrar
 * la pestaña con sendBeacon) a PHP/metricas/lote.php de corp-school, que las
 * inserta en metricas_uso (origen='web').
 *
 * Reglas: nunca lanza ni bloquea la app; sólo está activo en el host de
 * producción (environment.host); no guarda query strings ni ids en las rutas.
 */
export interface MedicionWeb {
    ts: number;
    pantalla: string;
    endpoint: string;
    ms: number;
    status: number;
    ok: boolean;
    extra?: any;
}

export interface MedicionEnCurso {
    fin: (ok: boolean, status?: number, extra?: any) => void;
    cancelar: () => void;
}

@Injectable()
export class MetricasService {
    /** Versión de Ventas en ejecución; la fija el header (donde vive la constante). */
    version: string = null;

    private readonly TIMEOUT_MS = 15000;
    private readonly INTERVALO_MS = 30000;
    private readonly MAX_BUFFER = 500;
    private readonly MAX_POR_LOTE = 200;
    private readonly MAX_BYTES_LOTE = 50000;

    private buffer: MedicionWeb[] = [];
    private iniciado = false;
    private url: string;
    private pantallaActual = '/';
    private navInicio = 0;
    private habilitado: boolean;

    constructor(private router: Router, private zone: NgZone) {
        const cfg = (environment as any).metricas;
        this.url = cfg && cfg.url ? cfg.url : null;
        const host = (environment as any).host;
        // Sólo en producción: en localhost / previews no se ensucia la tabla.
        this.habilitado = !!(this.url && host && typeof window !== 'undefined' && window.location.hostname === host);
    }

    /** Idempotente. Lo llama AppComponent. */
    iniciar(): void {
        if (this.iniciado || !this.habilitado) {
            return;
        }
        this.iniciado = true;
        try {
            this.pantallaActual = this.normalizarRuta(this.router.url);
            this.router.events.subscribe((e: any) => {
                if (e instanceof NavigationStart) {
                    this.navInicio = Date.now();
                } else if (e instanceof NavigationEnd) {
                    this.pantallaActual = this.normalizarRuta(e.urlAfterRedirects);
                    if (this.navInicio) {
                        this.registrar('navegacion', Date.now() - this.navInicio, true, null, null);
                    }
                    this.navInicio = 0;
                } else if (e instanceof NavigationCancel || e instanceof NavigationError) {
                    this.navInicio = 0;
                }
            });
            // Fuera de la zona de Angular: los timers y listeners no deben disparar detección de cambios.
            this.zone.runOutsideAngular(() => {
                setInterval(() => this.enviar(false), this.INTERVALO_MS);
                window.addEventListener('pagehide', () => this.enviar(true));
                window.addEventListener('beforeunload', () => this.enviar(true));
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState === 'hidden') {
                        this.enviar(true);
                    }
                });
            });
        } catch (e) { }
    }

    establecerVersion(version: string): void {
        this.version = version;
        establecerVersionSentry(version);
    }

    /** True si el endpoint es el de métricas (para que el interceptor no se mida a sí mismo). */
    esUrlDeMetricas(url: string): boolean {
        return !!this.url && !!url && url.indexOf(this.url) === 0;
    }

    estaHabilitado(): boolean {
        return this.habilitado;
    }

    /**
     * Empieza a medir algo. fin() registra la duración; si a los 15 s sigue sin
     * resolver se registra de inmediato como error (carga "colgada") y, cuando
     * termine, se registra también su duración real (también como error).
     */
    iniciarMedicion(endpoint: string): MedicionEnCurso {
        if (!this.habilitado) {
            return { fin: function () { }, cancelar: function () { } };
        }
        const t0 = Date.now();
        const pantalla = this.pantallaActual;
        let terminado = false;
        let timer: any = null;
        this.zone.runOutsideAngular(() => {
            timer = setTimeout(() => {
                if (!terminado) {
                    this.registrarEn(pantalla, endpoint, Date.now() - t0, false, null, { pendiente: true });
                }
            }, this.TIMEOUT_MS);
        });
        const limpiar = () => {
            terminado = true;
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        };
        return {
            fin: (ok: boolean, status?: number, extra?: any) => {
                if (terminado) {
                    return;
                }
                limpiar();
                const ms = Date.now() - t0;
                this.registrarEn(pantalla, endpoint, ms, ok && ms <= this.TIMEOUT_MS, status === undefined ? null : status, extra || null);
            },
            cancelar: limpiar
        };
    }

    /**
     * Envuelve una lectura (Firestore/RTDB) y mide hasta su primera emisión.
     * No cambia lo que emite ni sus errores.
     *   this.metricas.medirLectura('confirmaciones/doc', obs$).take(1)
     */
    medirLectura<T>(nombre: string, fuente: Observable<T>): Observable<T> {
        if (!this.habilitado) {
            return fuente;
        }
        return Observable.defer(() => {
            const m = this.iniciarMedicion('lectura:' + nombre);
            let medido = false;
            return fuente
                .do(
                    () => {
                        if (!medido) {
                            medido = true;
                            m.fin(true, null);
                        }
                    },
                    (err: any) => {
                        if (!medido) {
                            medido = true;
                            m.fin(false, null, { error: this.mensaje(err) });
                        }
                    }
                )
                .finally(() => m.cancelar());
        });
    }

    /** Registra una medición ya terminada (la usa el interceptor HTTP). */
    registrar(endpoint: string, ms: number, ok: boolean, status: number, extra: any): void {
        this.registrarEn(this.pantallaActual, endpoint, ms, ok, status, extra);
    }

    /** Quita ids y query de una ruta: /ventas/detalle-confirmacion/AbC123... -> /ventas/detalle-confirmacion/:id */
    normalizarRuta(url: string): string {
        try {
            let u = (url || '/').split('?')[0].split('#')[0];
            const partes = u.split('/').map((p) => {
                return (p.length >= 12 && /^[-_A-Za-z0-9]+$/.test(p) && /[0-9]/.test(p)) ? ':id' : p;
            });
            u = partes.join('/');
            return u.length > 191 ? u.substring(0, 191) : u;
        } catch (e) {
            return '/';
        }
    }

    /**
     * Endpoint de una petición: sin query ni fragmento. Del propio host (o relativas) se
     * guarda la ruta con los ids colapsados a ':id'; de hosts de terceros (HubSpot, etc.)
     * sólo el host, porque su ruta puede traer ids de clientes/contactos.
     */
    normalizarUrl(url: string): string {
        try {
            const u = (url || '').split('?')[0].split('#')[0];
            const m = /^(?:https?:)?\/\/([^\/]+)(\/.*)?$/i.exec(u);
            let res: string;
            if (!m) {
                res = this.normalizarRuta(u);
            } else if (m[1].toLowerCase() === String((environment as any).host || '').toLowerCase()) {
                res = m[1] + this.normalizarRuta(m[2] || '/');
            } else {
                res = m[1];
            }
            return res.length > 191 ? res.substring(0, 191) : res;
        } catch (e) {
            return 'url';
        }
    }

    private registrarEn(pantalla: string, endpoint: string, ms: number, ok: boolean, status: number, extra: any): void {
        if (!this.habilitado) {
            return;
        }
        try {
            this.buffer.push({
                ts: Date.now(),
                pantalla: pantalla,
                endpoint: endpoint,
                ms: Math.max(0, Math.round(ms)),
                status: status,
                ok: ok,
                extra: extra || undefined
            });
            if (this.buffer.length > this.MAX_BUFFER) {
                this.buffer.splice(0, this.buffer.length - this.MAX_BUFFER);
            }
        } catch (e) { }
    }

    private mensaje(err: any): string {
        try {
            const m = err && (err.code || err.message) ? String(err.code || err.message) : String(err);
            return m.substring(0, 120);
        } catch (e) {
            return 'error';
        }
    }

    private usuario(): string {
        try {
            const uid = window.localStorage.getItem('usuario.uid');
            return uid && uid !== 'null' ? uid : null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Manda el buffer en lotes. Cada lote se limita a ~50 KB (sendBeacon admite ~64 KB y falla
     * en silencio por encima). Los items que no se pudieron enviar vuelven al buffer. Al cerrar
     * se usa sendBeacon (sobrevive al cierre de la pestaña); si no está disponible o rechaza el
     * lote, los items se conservan.
     */
    private enviar(alCerrar: boolean): void {
        if (!this.habilitado || this.buffer.length === 0) {
            return;
        }
        try {
            // Al cerrar: hasta 10 beacons; en el intervalo normal: un lote por tick.
            const maxLotes = alCerrar ? 10 : 1;
            for (let n = 0; n < maxLotes && this.buffer.length > 0; n++) {
                const items = this.tomarLote();
                const cuerpo = JSON.stringify({ usuario: this.usuario(), version: this.version, items: items });
                if (alCerrar) {
                    const aceptado = typeof navigator !== 'undefined' && !!navigator.sendBeacon && navigator.sendBeacon(this.url, cuerpo);
                    if (!aceptado) {
                        this.devolver(items);
                        return;
                    }
                } else {
                    this.enviarXhr(items, cuerpo);
                }
            }
        } catch (e) { }
    }

    /** Saca del buffer hasta MAX_POR_LOTE items sin pasar de ~50 KB de JSON. */
    private tomarLote(): MedicionWeb[] {
        const items: MedicionWeb[] = [];
        let bytes = 200;
        while (this.buffer.length > 0 && items.length < this.MAX_POR_LOTE) {
            const tam = JSON.stringify(this.buffer[0]).length + 1;
            if (items.length > 0 && bytes + tam > this.MAX_BYTES_LOTE) {
                break;
            }
            bytes += tam;
            items.push(this.buffer.shift());
        }
        return items;
    }

    /** Devuelve al frente del buffer los items no enviados (respetando el tope). */
    private devolver(items: MedicionWeb[]): void {
        this.buffer = items.concat(this.buffer);
        if (this.buffer.length > this.MAX_BUFFER) {
            this.buffer.splice(0, this.buffer.length - this.MAX_BUFFER);
        }
    }

    private enviarXhr(items: MedicionWeb[], cuerpo: string): void {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', this.url, true);
        // text/plain: petición "simple" (sin preflight CORS); lote.php decodifica el JSON.
        xhr.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4 && (xhr.status === 0 || xhr.status >= 500)) {
                // Red caída o servidor fallando: se reintenta en el siguiente tick (buffer acotado).
                this.devolver(items);
            }
        };
        xhr.send(cuerpo);
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/monitoring/metricas.service.ts